// ============================================================================
// Which stock locations serve a clinic (TCSD-0388), for the delivery and pickup
// windows. The same rule the Co-pilot card uses (am-copilot stockSourceChoices):
// every location marked 'stock' in the same region as the clinic's own location.
// The Dock only receives freight and is never offered (TCSD-0318). The clinic's
// own location comes first (Kansas City and Omaha → KC Headquarters, TCSD-0389).
// ============================================================================

import type { LocationStockRow, OfficeRow } from './api';
import { fmtQty } from './format';

export interface SourceChoice {
  office_id: string;
  office_name: string;
  isDefault: boolean;
  /** supply_id → on hand, from v_stock_location_stock. Missing = nothing recorded = 0. */
  onHand: Record<string, number>;
}

export function sourceChoices(
  offices: OfficeRow[],
  clinicOfficeId: string | null | undefined,
  stock: LocationStockRow[],
): SourceChoice[] {
  const own = offices.find((o) => o.office_id === clinicOfficeId) ?? null;
  const inRegion = own?.office_region
    ? offices.filter((o) => o.stock_role === 'stock' && o.office_region === own.office_region)
    : offices.filter((o) => o.stock_role === 'stock'); // no location set: every stock location, as before
  if (own && own.stock_role === 'stock' && !inRegion.some((o) => o.office_id === own.office_id)) inRegion.push(own);
  return inRegion
    .map((o) => ({
      office_id: o.office_id ?? '',
      office_name: o.office_name ?? o.office_id ?? '',
      isDefault: o.office_id === clinicOfficeId,
      onHand: Object.fromEntries(
        stock.filter((s) => s.office_id === o.office_id).map((s) => [s.supply_id ?? '', Number(s.on_hand ?? 0)]),
      ),
    }))
    .sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || a.office_name.localeCompare(b.office_name));
}

/** "500 tabs · 0 gal" for the products asked about. */
export function onHandText(c: SourceChoice, products: Array<{ supply_id: string | null; unit_label: string | null }>): string {
  return products.map((p) => fmtQty(c.onHand[p.supply_id ?? ''] ?? 0, p.unit_label)).join(' · ');
}
